import React, { useState, useEffect, useMemo } from "react";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import {
  deletePurchase,
  getPurchaseByTransType,
} from "../../services/PurchaseApiService";
import PurchaseOrderModal from "../../components/transactions/modal/PurchaseOrderModal";
import { useDispatch, useSelector } from "react-redux";
import { PurchaseClass } from "../../core/json/Purchase";
import { fetchProduct, fetchSupplier, fetchTax, fetchUnit } from "../../redux/userSetting";
import { fetchBranchSettings } from "../../redux/settingbyBranch";
import { getWareHouseList } from "../../services/MasterApiServices";
import {
  TransactionScreen,
  PURCHASE_PAYMENTS,
} from "../../components/transactions/list";
import { dateOf, dmy, intg, money, num } from "../../components/report/reportFormat";

const PurchaseOrder = () => {
  const [purchaseData, setPurchaseData] = useState([]);
  const [updateData, setUpdateData] = useState([]);
  const [modalMode, setModalMode] = useState("add");
  const [modalOpen, setModalOpen] = useState(false);
  const { menuItems } = useSelector((state) => state.amc);
  const [permit, setpermit] = useState();
  const [wareHouseList, setWareHouseList] = useState([]);

  const { branch } = useSelector((state) => state.branchlist);
  const { userId } = useSelector((state) => state.userauth);
  const { supplierList } = useSelector((state) => state.usersetting);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const response = await getWareHouseList();
        setWareHouseList(Array.isArray(response) ? response : []);
      } catch (err) {
        console.error("Failed to fetch warehouses:", err);
      }
    };
    fetchWarehouses();
  }, []);

  useEffect(() => {
    setpermit(
      menuItems?.menuPermissions?.find((e) => e.menu.name == "Purchase Return")
    );
    dispatch(fetchSupplier());
    dispatch(fetchUnit());
    dispatch(fetchTax());
  }, []);
  useEffect(() => {
    handlePurchaseData();
    dispatch(fetchProduct(branch));
    dispatch(fetchBranchSettings(branch.guid));
  }, [branch]);

  // Fetch purchase orders data
  const handlePurchaseData = async () => {
    try {
      const response = await getPurchaseByTransType(branch, 0);
      const invoice = Array.isArray(response.data) ? response.data : [];
      setPurchaseData(invoice);
    } catch (error) {
      console.error("Failed to fetch purchase orders", error);
    }
  };

  const MySwal = withReactContent(Swal);

  // Handle the click event to delete a Purchase Invoice
  const handleDeleteClick = (obj) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      cancelButtonColor: "#ff0000",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const objPurchase = new PurchaseClass();
          objPurchase.id = obj.id;
          objPurchase.guid = obj.guid;
          objPurchase.branch = branch;
          objPurchase.isDeleted = true;
          objPurchase.updatedUser = `{${userId?.id}}`;
          const response = await deletePurchase(objPurchase);
          if (response?.data === true) {
            MySwal.fire({
              title: "Deleted!",
              text: "The Order has been deleted.",
              icon: "success",
              confirmButtonColor: "#00ff00",
              confirmButtonText: "OK",
            }).then(() => {
              handlePurchaseData(); // Refresh the list
            });
          } else {
            MySwal.fire({
              title: "Error!",
              text: "Something went wrong.",
              icon: "error",
              confirmButtonColor: "#ff0000",
              confirmButtonText: "OK",
            });
          }
        } catch (error) {
          console.error("Error deleting reason:", error);
          MySwal.fire({
            title: "Error!",
            text: "Something went wrong.",
            icon: "error",
            confirmButtonColor: "#ff0000",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const handleEditClick = async (guid) => {
    try {
      const response = getPurchaseInvoiceByGuid(guid);

      setUpdateData(response);
      setModalMode("edit");

      setModalOpen(true);
    } catch (error) {
      console.error("Error fetching purchase order by GUID", error);
    }
  };

  function getPurchaseInvoiceByGuid(guid) {
    const filteredPurchase = purchaseData?.filter(
      (purchase) => purchase.guid === guid
    );

    return filteredPurchase[0];
  }

  /* ---------------- list ---------------- */
  const warehouseMap = useMemo(
    () => Object.fromEntries(wareHouseList.map((w) => [w.guid, w.name || "Unnamed Warehouse"])),
    [wareHouseList]
  );

  // one flat record per order; the keys match the column definitions
  const rows = useMemo(
    () =>
      purchaseData.map((inv) => {
        const pay = PURCHASE_PAYMENTS.find((p) => p.v === inv.purchasePaymentType);
        return {
          guid: inv.guid,
          raw: inv,
          date: dateOf(inv.date),
          billNo: inv.entrynumber || "",
          supplier: inv.supplier?.name || "Unknown Supplier",
          supplierGuid: inv.supplier?.guid,
          deliveryDate: dateOf(inv.duedate),
          warehouse: warehouseMap[inv.wareHouse?.guid] || inv.wareHouse?.name || "",
          warehouseGuid: inv.wareHouse?.guid,
          payment: pay?.t || "Unknown",
          paymentType: inv.purchasePaymentType,
          items: (inv.purchaseDetails || []).length,
          total: num(inv.grossTotal),
          additional: num(inv.additionalAmt),
          discount: num(inv.discountTotal),
          roundOff: num(inv.roundOff),
          tax: num(inv.taxTotal),
          net: num(inv.netTotal),
          lines: inv.purchaseDetails || [],
        };
      }),
    [purchaseData, warehouseMap]
  );

  const columns = useMemo(
    () => [
      { k: "date", t: "Date", type: "date", ago: true },
      { k: "billNo", t: "Number", type: "doc" },
      { k: "supplier", t: "Supplier", type: "party" },
      { k: "deliveryDate", t: "Delivery Date", type: "date" },
      {
        k: "payment",
        t: "Status",
        type: "chip",
        chip: (r) => ({ text: r.payment, tone: PURCHASE_PAYMENTS.find((p) => p.v === r.paymentType)?.tone }),
      },
      { k: "warehouse", t: "Warehouse", off: true },
      { k: "items", t: "Items", type: "int", off: true },
      { k: "total", t: "Gross", type: "money", off: true },
      { k: "discount", t: "Discount", type: "money", off: true },
      { k: "tax", t: "Tax", type: "money", off: true },
      { k: "net", t: "Net Amount", type: "money" },
    ],
    []
  );

  const tabs = useMemo(() => [{ id: "all", label: "Purchase Order" }], []);

  const filters = useMemo(
    () => [
      {
        key: "suppliers",
        label: "Supplier",
        items: (supplierList || []).map((s) => ({ v: s.guid, t: s.name || "—" })),
        match: (r, v) => v.includes(r.supplierGuid),
      },
      {
        key: "warehouses",
        label: "Warehouse",
        items: wareHouseList.map((w) => ({ v: w.guid, t: w.name || "Unnamed Warehouse" })),
        match: (r, v) => v.includes(r.warehouseGuid),
      },
      {
        key: "payments",
        label: "Payment type",
        items: PURCHASE_PAYMENTS.map((p) => ({ v: p.v, t: p.t })),
        match: (r, v) => v.includes(r.paymentType),
      },
    ],
    [supplierList, wareHouseList]
  );

  const searchMatch = (r, q) => {
    const hit = (s) => String(s || "").toLowerCase().includes(q);
    return (
      hit(r.billNo) ||
      hit(r.supplier) ||
      hit(r.payment) ||
      hit(r.date) ||
      String(r.net).includes(q) ||
      r.lines.some((l) => hit(l?.product?.name) || hit(l?.product?.code))
    );
  };

  const drawerContent = (r) => ({
    title: r.supplier,
    subtitle: [r.billNo, dmy(r.date)].filter(Boolean).join("  ·  "),
    avatarKey: r.supplier,
    meta: [
      { k: "Warehouse", v: r.warehouse },
      { k: "Status", v: r.payment },
      { k: "Delivery date", v: r.deliveryDate ? dmy(r.deliveryDate) : "" },
      { k: "Items", v: intg(r.items) },
    ],
    lines: {
      title: "Order lines",
      columns: [
        { t: "Item" },
        { t: "Qty", align: "right" },
        { t: "Rate", align: "right" },
        { t: "Disc", align: "right" },
        { t: "Amount", align: "right" },
      ],
      rows: r.lines.map((l) => [
        <>
          <div className="axr-nm">{l?.product?.name || "Unknown Item"}</div>
          <div className="axr-cd">{[l?.product?.code, l?.unit?.name].filter(Boolean).join(" · ")}</div>
        </>,
        intg(l?.quantity),
        money(l?.price),
        num(l?.discountAmount) ? money(l.discountAmount) : "—",
        money(l?.netAmount),
      ]),
    },
    totals: [
      { l: "Gross", v: money(r.total) },
      { l: "Discount", v: r.discount ? "−" + money(r.discount) : "0.00" },
      { l: "Additional", v: money(r.additional) },
      { l: "Tax", v: money(r.tax) },
      { l: "Round off", v: money(r.roundOff) },
    ],
    net: { l: "Net amount", v: money(r.net) },
  });

  return (
    <TransactionScreen
      title="Purchase Order"
      subtitle="Manage purchase orders here"
      unit="orders"
      rows={rows}
      columns={columns}
      tabs={tabs}
      filters={filters}
      searchMatch={searchMatch}
      searchPlaceholder="Order no, supplier, item, amount…"
      sortOptions={[
        { v: "date:-1", t: "Newest first" },
        { v: "date:1", t: "Oldest first" },
        { v: "net:-1", t: "Largest amount" },
        { v: "net:1", t: "Smallest amount" },
        { v: "supplier:1", t: "Supplier A–Z" },
      ]}
      exportName="Purchase Order"
      onRefresh={handlePurchaseData}
      canAdd={permit?.add}
      newLabel="New order"
      onNew={() => {
        setModalMode("add");
        setModalOpen(true);
      }}
      canEdit={permit?.edit}
      onEdit={(r) => handleEditClick(r.guid)}
      canDelete={permit?.deletion}
      onDelete={(r) => handleDeleteClick(r.raw)}
      drawerContent={drawerContent}
      keysDisabled={modalOpen}
    >
      {modalOpen && (
        <PurchaseOrderModal
          mode={modalMode}
          data={modalMode === "edit" ? updateData : {}}
          handleClose={() => setModalOpen(false)}
          handleRefresh={() => {
            handlePurchaseData();
          }}
        />
      )}
    </TransactionScreen>
  );
};

export default PurchaseOrder;
